import type { CallSequenceExample, CallSequenceSettings, OrganizationSettingsView, SaveCallSequenceSettingsBody } from "@gioia365/contracts";
import { sequenceExample } from "@gioia365/domain/call-sequence-example";
import type { MessageKey } from "@gioia365/i18n";
import { formatNumericDateTime } from "../../i18n/format";
import type { Translate } from "../../i18n/locale";
import { draftFromSettings, WEEKDAY_KEYS, type WeekdayKey } from "./calendar-draft";

// La bozza di «Sequenza di chiamate» (piano M5, Task 13, sul modello di `calendar-draft.ts` di M1b): le modifiche sono i soli
// campi toccati sopra la regola salvata, così una rilettura dopo un conflitto (`409 version_conflict`) rimette le modifiche
// sopra la regola nuova e i campi non toccati prendono quella. Funzioni pure: la bozza dalla regola salvata, i problemi che
// impediscono di salvare (gli stessi confini del server, sul calendario dell'azienda), ciò che cambia detto a parole, l'esempio di
// un Lead che entra adesso e il corpo della richiesta.

/** I campi toccati, sopra la regola salvata. Un campo tornato uguale al salvato non è una modifica (`describeSequenceChanges`). */
export type CallSequenceEdits = Partial<CallSequenceSettings>;

/** Le chiamate di fila di un tentativo aggiunto con «+ Aggiungi un tentativo», come nel mock-up. */
export const ADDED_ATTEMPT_CALLS = 2;

/** La regola che la pagina mostra: quella salvata con sopra le modifiche. */
export function draftOf(saved: CallSequenceSettings, edits: CallSequenceEdits): CallSequenceSettings {
  return { ...saved, ...edits, attempts: [...(edits.attempts ?? saved.attempts)] };
}

/**
 * Le modifiche dopo `update`, senza i campi tornati uguali a quelli salvati (i tentativi confrontati per contenuto): un valore
 * cambiato e rimesso com'era non resta nella bozza, così una regola riletta dopo, salvata da un collega, non viene coperta da
 * una modifica che la persona aveva già annullato (rilievo di Codex sulla #92, turno di notte del 10 ottobre 2026).
 */
export function editsWith(saved: CallSequenceSettings, edits: CallSequenceEdits, update: CallSequenceEdits): CallSequenceEdits {
  const next: Record<string, unknown> = { ...edits, ...update };
  for (const key of Object.keys(next) as Array<keyof CallSequenceSettings>) {
    const value = next[key];
    const same =
      key === "attempts"
        ? Array.isArray(value) && value.length === saved.attempts.length && value.every((count, at) => count === saved.attempts[at])
        : value === saved[key];
    if (same) delete next[key];
  }
  return next as CallSequenceEdits;
}

/** Il corpo di `PUT /api/settings/call-sequence`: la regola intera e la versione su cui la bozza è stata pensata. */
export function saveBodyOf(draft: CallSequenceSettings, expectedVersion: number): SaveCallSequenceSettingsBody {
  return { settings: { ...draft, attempts: [...draft.attempts] }, expectedVersion };
}

const callsText = (t: Translate, count: number) => (count === 1 ? t("callSequenceSettings.callsOne") : t("callSequenceSettings.callsMany", { count }));
const countdownText = (t: Translate, seconds: number) =>
  seconds === 0 ? t("callSequenceSettings.changes.countdownNone") : t("callSequenceSettings.countdownSeconds", { seconds });
/** «subito», «dopo 3 secondi», «lo passo io» (`null`): le parole di «Dopo la chiamata, passa al Lead successivo» dentro una frase. */
const advanceText = (t: Translate, seconds: number | null) => {
  if (seconds === null) return t("callSequenceSettings.changes.advanceManual");
  return seconds === 0 ? t("callSequenceSettings.changes.advanceNow") : t("callSequenceSettings.changes.advanceSeconds", { seconds });
};
/** «14 giorni lavorativi», «mai» (`null`): le parole di «Senza contatto, la sequenza riparte da capo dopo» dentro una frase. */
const restartText = (t: Translate, days: number | null) =>
  days === null ? t("callSequenceSettings.changes.restartNever") : t("callSequenceSettings.changes.restartDays", { days });

/** Ciò che cambia rispetto alla regola salvata, a parole e nell'ordine del mock-up: ogni frase è una modifica. */
export function describeSequenceChanges(saved: CallSequenceSettings, draft: CallSequenceSettings, t: Translate): string[] {
  const sentences: string[] = [];
  if (saved.attempts.length !== draft.attempts.length) {
    sentences.push(t("callSequenceSettings.changes.attempts", { before: saved.attempts.length, after: draft.attempts.length }));
  }
  draft.attempts.forEach((calls, index) => {
    const was = saved.attempts[index];
    if (was === undefined) sentences.push(t("callSequenceSettings.changes.newAttempt", { n: index + 1, calls: callsText(t, calls) }));
    else if (was !== calls) sentences.push(t("callSequenceSettings.changes.attemptCalls", { n: index + 1, after: calls, before: was }));
  });
  if (saved.morningEnd !== draft.morningEnd) sentences.push(t("callSequenceSettings.changes.morningEnd", { after: draft.morningEnd, before: saved.morningEnd }));
  if (saved.afternoonStart !== draft.afternoonStart) {
    sentences.push(t("callSequenceSettings.changes.afternoonStart", { after: draft.afternoonStart, before: saved.afternoonStart }));
  }
  if (saved.firstCallMinutes !== draft.firstCallMinutes) {
    sentences.push(t("callSequenceSettings.changes.firstCall", { after: draft.firstCallMinutes, before: saved.firstCallMinutes }));
  }
  if (saved.fridayOne !== draft.fridayOne) sentences.push(t(draft.fridayOne ? "callSequenceSettings.changes.fridayOneOn" : "callSequenceSettings.changes.fridayOneOff"));
  if (saved.restartAfterWorkingDays !== draft.restartAfterWorkingDays) {
    sentences.push(
      t("callSequenceSettings.changes.restart", { after: restartText(t, draft.restartAfterWorkingDays), before: restartText(t, saved.restartAfterWorkingDays) }),
    );
  }
  if (saved.countdownSeconds !== draft.countdownSeconds) {
    sentences.push(t("callSequenceSettings.changes.countdown", { after: countdownText(t, draft.countdownSeconds), before: countdownText(t, saved.countdownSeconds) }));
  }
  if (saved.advanceSeconds !== draft.advanceSeconds) {
    sentences.push(t("callSequenceSettings.changes.advance", { after: advanceText(t, draft.advanceSeconds), before: advanceText(t, saved.advanceSeconds) }));
  }
  if (saved.autoRedial !== draft.autoRedial) {
    const word = (on: boolean) => t(on ? "callSequenceSettings.changes.yes" : "callSequenceSettings.changes.no");
    sentences.push(t("callSequenceSettings.changes.autoRedial", { after: word(draft.autoRedial), before: word(saved.autoRedial) }));
  }
  return sentences;
}

export type SequenceProblem = { code: "afternoon_before_morning" } | { code: "day_inside_pause"; day: WeekdayKey; start: string; end: string };

const minutesOf = (clock: string) => Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5));

/**
 * Ciò che impedisce di salvare, come lo controlla il server e nello stesso ordine (ruling Q44): il pomeriggio comincia quando
 * finisce la mattina o dopo, e nessun giorno aperto cade tutto dentro la pausa `[morningEnd, afternoonStart)`, dove non avrebbe
 * nessuna mezza giornata. Un giorno con la sola mattina (il sabato 09:00–12:00) o il solo pomeriggio va bene. Il calendario è quello
 * dell'azienda, con i giorni omessi completati dall'orario di partenza (`draftFromSettings`).
 */
export function sequenceProblem(draft: CallSequenceSettings, calendar: Pick<OrganizationSettingsView, "timeZone" | "defaultLocale" | "workingCalendar">): SequenceProblem | null {
  const morningEnd = minutesOf(draft.morningEnd);
  const afternoonStart = minutesOf(draft.afternoonStart);
  if (afternoonStart < morningEnd) return { code: "afternoon_before_morning" };
  const { weeklyHours } = draftFromSettings(calendar);
  const day = WEEKDAY_KEYS.find((key) => {
    const hours = weeklyHours[key];
    return hours.open && minutesOf(hours.start) >= morningEnd && minutesOf(hours.end) <= afternoonStart;
  });
  return day ? { code: "day_inside_pause", day, start: weeklyHours[day].start, end: weeklyHours[day].end } : null;
}

/** «del sabato», per la frase del giorno nella pausa: le parole del calendario. */
const WEEKDAY_OF = {
  "1": "calendar.weekdays.of.1",
  "2": "calendar.weekdays.of.2",
  "3": "calendar.weekdays.of.3",
  "4": "calendar.weekdays.of.4",
  "5": "calendar.weekdays.of.5",
  "6": "calendar.weekdays.of.6",
  "7": "calendar.weekdays.of.7",
} as const satisfies Record<WeekdayKey, MessageKey>;

/** Il problema a parole, per la barra della bozza. */
export function problemText(t: Translate, problem: SequenceProblem): string {
  if (problem.code === "afternoon_before_morning") return t("callSequenceSettings.problems.afternoonBeforeMorning");
  return t("callSequenceSettings.problems.dayInsidePause", { day: t(WEEKDAY_OF[problem.day]), start: problem.start, end: problem.end });
}

/**
 * L'esempio di un Lead che entra a `now` con la regola della bozza, sul calendario dell'azienda, con le stesse mezze giornate
 * del server (`sequenceExample` del dominio): la pagina lo mostra quando la bozza cambia qualcosa, e altrimenti quello del server.
 */
export function draftExample(draft: CallSequenceSettings, calendar: Pick<OrganizationSettingsView, "timeZone" | "workingCalendar">, now: Date): CallSequenceExample[] {
  const { weeklyHours, closedDates } = calendar.workingCalendar;
  return sequenceExample(draft, { timeZone: calendar.timeZone, weeklyHours, closedDates }, now).map((attempt) => ({
    attempt: attempt.attempt,
    halfDay: attempt.halfDay,
    day: attempt.day,
    deadline: attempt.deadline?.toISOString() ?? null,
  }));
}

const WEEKDAY_WORD = {
  "1": "callSequenceSettings.weekday.1",
  "2": "callSequenceSettings.weekday.2",
  "3": "callSequenceSettings.weekday.3",
  "4": "callSequenceSettings.weekday.4",
  "5": "callSequenceSettings.weekday.5",
  "6": "callSequenceSettings.weekday.6",
  "7": "callSequenceSettings.weekday.7",
} as const satisfies Record<WeekdayKey, MessageKey>;
const HALF_DAY_WORD = { morning: "callSequenceSettings.halfDay.morning", afternoon: "callSequenceSettings.halfDay.afternoon" } as const satisfies Record<
  CallSequenceExample["halfDay"],
  MessageKey
>;

/**
 * «giovedì 08/10», come nel mock-up: il giorno della settimana e la data in numeri, senza l'anno. Lo usa anche la sezione
 * «Sequenza di chiamate» della scheda Lead per le mezze giornate (`sequence-texts.ts`).
 */
export function dayWords(t: Translate, day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  const weekday = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, date ?? 1)).getUTCDay();
  const key = WEEKDAY_KEYS[(weekday + 6) % 7] ?? "1";
  return t("callSequenceSettings.exampleDay", { weekday: t(WEEKDAY_WORD[key]), date: `${day.slice(8, 10)}/${day.slice(5, 7)}` });
}

/** Le righe dell'esempio: il primo tentativo con l'ora entro cui fare la prima chiamata, nel fuso dell'azienda. */
export function exampleLines(t: Translate, example: readonly CallSequenceExample[], timeZone: string): string[] {
  return example.map((attempt) => {
    const words = { day: dayWords(t, attempt.day), halfDay: t(HALF_DAY_WORD[attempt.halfDay]) };
    return attempt.deadline !== null
      ? t("callSequenceSettings.exampleFirst", { ...words, time: formatNumericDateTime(timeZone, attempt.deadline).slice(-5) })
      : t("callSequenceSettings.exampleRow", { ...words, n: attempt.attempt });
  });
}

/** «14 chiamate in 3 giorni lavorativi»: le chiamate della regola e i giorni dell'esempio; `null` senza esempio. */
export function totalText(t: Translate, draft: CallSequenceSettings, example: readonly CallSequenceExample[]): string | null {
  if (example.length === 0) return null;
  const calls = draft.attempts.reduce((sum, count) => sum + count, 0);
  const days = new Set(example.map((attempt) => attempt.day)).size;
  if (days === 1) return calls === 1 ? t("callSequenceSettings.totalOne") : t("callSequenceSettings.totalOneDay", { calls });
  return t("callSequenceSettings.total", { calls, days });
}
